import type { DocumentKindSentence } from "encamp-shared/src/waste/shippingDocumentLabels";

import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { ManifestShipmentLinkQuery } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";

import { formatQuantity } from "../Containers/containerDisplay";
import { formatShipmentDate } from "../Shipments/shipmentDisplay";
import { ShipmentReconciliationChip } from "../Shipments/ShipmentReconciliationChip";
import { WasteShipmentStatusChip } from "../Shipments/WasteShipmentStatusChip";

// Named off the manifest query, which writes the selection out in full.
type ManifestRow = NonNullable<ManifestShipmentLinkQuery["processedManifests"]>["items"][number];
export type LinkedShipment = NonNullable<ManifestRow["linkedShipment"]>;
export type LinkedContainer = ManifestRow["linkedContainers"][number];
export type CandidateShipment = ManifestRow["candidateShipments"][number];

/**
 * "Shipped 08/04/2026" for a shipment that has left, "Planned 08/12/2026" for
 * one that has not, and null for a shipment carrying neither date.
 *
 * Null rather than a bare label: a requested shipment with no planned date is
 * ordinary — the request may have been phoned in — and labelling it "Shipped"
 * with an empty date claimed the waste had left, which is the one thing a
 * compliance record must not say by accident.
 */
function shipmentDateLabel(shipment: {
  shippedDate?: string | null;
  plannedShipDate?: string | null;
}): string | null {
  if (shipment.shippedDate) {
    return `Shipped ${formatShipmentDate(shipment.shippedDate)}`;
  }
  if (shipment.plannedShipDate) {
    return `Planned ${formatShipmentDate(shipment.plannedShipDate)}`;
  }
  return null;
}

/** The shipment this manifest was attached to, and the containers it carried. */
export function LinkedShipmentSummary({
  shipment,
  containers,
  tenantId,
}: {
  shipment: LinkedShipment;
  containers: readonly LinkedContainer[];
  tenantId: string | undefined;
}) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2">
          {tenantId ? (
            <RouterLink to={`/o/${tenantId}/waste/shipments/${shipment.id}`}>
              {shipment.manifestRequestNumber}
            </RouterLink>
          ) : (
            shipment.manifestRequestNumber
          )}
        </Typography>
        <WasteShipmentStatusChip status={shipment.status} />
        <ShipmentReconciliationChip status={shipment.reconciliation.status} showNone />
        {shipmentDateLabel(shipment) !== null && (
          <Typography variant="caption" color="text.secondary">
            {shipmentDateLabel(shipment)}
          </Typography>
        )}
      </Stack>

      {containers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No containers on the attached shipment.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            {containers.length} {containers.length === 1 ? "container" : "containers"}
          </Typography>
          {containers.map((container) => (
            <Stack
              key={container.id}
              direction="row"
              spacing={1.5}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2">
                {tenantId ? (
                  <RouterLink to={`/o/${tenantId}/waste/containers/${container.id}`}>
                    {container.containerLabel}
                  </RouterLink>
                ) : (
                  container.containerLabel
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[
                  container.accumulationArea?.name,
                  formatQuantity(container.amount, container.amountUnit),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The shipments this manifest might belong to.
 *
 * Where auto-match hands off: it links a pair only when each side has exactly
 * one plausible counterpart, so anything ambiguous — two look-alike shipments a
 * few days apart, say — lands here for a person to settle.
 */
export function CandidateShipmentList({
  candidates,
  tenantId,
  attaching,
  onLink,
  paperName = "Manifest",
}: {
  paperName?: DocumentKindSentence;
  candidates: readonly CandidateShipment[];
  tenantId: string | undefined;
  attaching: boolean;
  onLink: (shipmentId: string) => void;
}) {
  return (
    <Stack spacing={1.5}>
      {/* "Requested or shipped", which is what the loader actually offers.
          "Open" was wrong in both directions: it excluded drafts, which are the
          open ones, and described shipped shipments — most candidates — as
          though the waste had not gone yet. */}
      <Alert severity="info">
        This {paperName.toLowerCase()} is not linked to a shipment. These requested or shipped
        shipments at the same facility are close enough in date to be plausible — linking one
        matches its containers to this {paperName.toLowerCase()}&apos;s waste lines.
      </Alert>
      {candidates.map((candidate) => (
        <Stack
          key={candidate.id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2">
            {tenantId ? (
              <RouterLink to={`/o/${tenantId}/waste/shipments/${candidate.id}`}>
                {candidate.manifestRequestNumber}
              </RouterLink>
            ) : (
              candidate.manifestRequestNumber
            )}
          </Typography>
          <WasteShipmentStatusChip status={candidate.status} />
          <Typography variant="caption" color="text.secondary">
            {[
              `${candidate.lines.length} ${
                candidate.lines.length === 1 ? "container" : "containers"
              }`,
              shipmentDateLabel(candidate)?.toLowerCase(),
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" disabled={attaching} onClick={() => onLink(candidate.id)}>
            Link
          </Button>
        </Stack>
      ))}
    </Stack>
  );
}
